/**
 * Which language this device reads the base set in.
 *
 * Per device, not per account, and stored in `localStorage` next to the theme —
 * a phone that gets passed around a table belongs to whoever is holding it, and
 * the person handed it mid-round should not have the language change under them
 * because the seat changed. It is also the only place it *can* live and still be
 * right offline on a fresh install, before any account row has synced down.
 *
 * Nothing here writes to the database. `seed_key` on the row plus this setting
 * is the whole mechanism: the stored English text stays the durable copy, and
 * `shared/catalog.ts` resolves it at render time. So switching languages cannot
 * damage a record, and an export opened somewhere else is still readable.
 */

import { useSyncExternalStore } from "react";

import {
  DEFAULT_LOCALE,
  canonicalName,
  canonicalStops,
  isLocale,
  localiseName,
  localiseStops,
  matchLocale,
  type Locale,
} from "../shared/catalog.ts";
import type { Stop } from "../shared/types.ts";

const KEY = "pb-locale";

const listeners = new Set<() => void>();
let current: Locale | null = null;

/**
 * The browser's own preference, used only when nothing has been chosen here.
 *
 * Wrapped because this module is imported by code that runs under `bun test`
 * with no DOM at all, and a missing `navigator` is not an error worth having a
 * screen fail over — it is the test runner, and `DEFAULT_LOCALE` is a perfectly
 * good answer for it.
 */
function fromBrowser(): Locale {
  try {
    const nav = globalThis.navigator as Navigator | undefined;
    if (!nav) return DEFAULT_LOCALE;
    const tags = nav.languages?.length ? nav.languages : nav.language ? [nav.language] : [];
    return matchLocale(tags);
  } catch {
    return DEFAULT_LOCALE;
  }
}

function stored(): Locale | null {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    // Safari in a locked-down private window throws on the *getter*, not on the
    // call. Nothing to do about it but read the browser's preference instead.
    return null;
  }
}

export function getLocale(): Locale {
  current ??= stored() ?? fromBrowser();
  return current;
}

/** True when this device is following the browser rather than a chosen language. */
export function localeIsAutomatic(): boolean {
  return stored() === null;
}

export function setLocale(next: Locale): void {
  current = next;
  try {
    globalThis.localStorage?.setItem(KEY, next);
  } catch {
    // Unwritable storage costs the choice its persistence, not its effect: the
    // module-level `current` still holds it for this tab.
  }
  applyLang(next);
  for (const fn of [...listeners]) fn();
}

/** Keeps `<html lang>` honest — screen readers and hyphenation both read it. */
export function applyLang(locale: Locale = getLocale()): void {
  try {
    if (typeof document !== "undefined") document.documentElement.lang = locale;
  } catch {
    // No DOM. Nothing to label.
  }
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Re-renders the calling component when the language changes. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale);
}

// ---------------------------------------------------------------------------
// Applying it to rows
// ---------------------------------------------------------------------------

/** The two shapes that carry a frozen question: `SessionAspect`, `SessionSubQuestion`. */
interface Snapshot {
  name_snapshot: string;
  stops_snapshot: Stop[];
  seed_key_snapshot: string | null;
}

/**
 * One frozen question, read in this device's language.
 *
 * Deliberately returns a new object rather than mutating: the row it is given
 * came out of the store, and the store's copy is the record. Rewriting it in
 * place would mean an export — or the next comparison against the server —
 * carrying whichever language happened to be selected when the screen rendered.
 *
 * Cheap enough to run on every render of a night. A session holds tens of these,
 * not thousands, and the alternative is a cache keyed on both the row version and
 * the locale, which is more state than the work it saves.
 */
export function localiseSnapshot<T extends Snapshot>(row: T, locale: Locale): T {
  const key = row.seed_key_snapshot;
  if (!key) return row;
  return {
    ...row,
    name_snapshot: localiseName(locale, key, row.name_snapshot),
    stops_snapshot: localiseStops(locale, key, row.stops_snapshot),
  };
}

/** The same, for a live taxonomy row that has not been frozen into a night yet. */
export function localiseDefinition<T extends { name: string; stops: Stop[]; seed_key: string | null }>(
  row: T,
  locale: Locale,
): T {
  if (!row.seed_key) return row;
  return {
    ...row,
    name: localiseName(locale, row.seed_key, row.name),
    stops: localiseStops(locale, row.seed_key, row.stops),
  };
}

/**
 * A patch built from a form the reader saw translated, in the language stored.
 *
 * The question editor is fed localised rows, so what comes back out of it is
 * localised too. `shared/catalog.ts` explains why storing that verbatim would be
 * wrong; this is where the translation is undone, at the one point every edit
 * passes through, rather than in each of the two save handlers.
 */
export function canonicalisePatch(
  patch: Record<string, unknown>,
  seedKey: string | null | undefined,
  locale: Locale,
): Record<string, unknown> {
  if (!seedKey) return patch;
  const out = { ...patch };
  if (typeof out["name"] === "string") {
    out["name"] = canonicalName(locale, seedKey, out["name"]);
  }
  if (Array.isArray(out["stops"])) {
    out["stops"] = canonicalStops(locale, seedKey, out["stops"] as Stop[]);
  }
  return out;
}
